import { useEffect, useState } from "react";
import { Reveal } from "@/lib/motion";
import { getPricing } from "@/lib/api";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const fmt = (n, cur) => new Intl.NumberFormat(cur === "INR" ? "en-IN" : "en-US").format(n);

export default function BundlePricing({ defaultCurrency = "USD", onSelect, heading, subheading, className = "", accent = "#2563eb" }) {
  const [data, setData] = useState(null);
  const [cur, setCur] = useState(defaultCurrency);

  useEffect(() => {
    getPricing().then(setData).catch(() => {});
  }, []);

  useEffect(() => { setCur(defaultCurrency); }, [defaultCurrency]);

  const packages = data?.packages || [];
  const symbols = data?.symbols || { INR: "\u20b9", USD: "$" };
  const currencies = data?.currencies || ["INR", "USD"];

  return (
    <section className={`relative z-10 py-20 md:py-28 bg-slate-50 border-y border-slate-100 ${className}`} data-testid="bundle-pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <span className="overline">Service Bundles</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-3 max-w-3xl mx-auto">
            {heading || "One partner. Every stage of growth."}
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-base">
            {subheading || "Start with the essentials and scale into a fully managed acquisition engine. Prices shown per month."}
          </p>

          <div className="inline-flex mt-7 rounded-full border border-slate-200 bg-white p-1" data-testid="pricing-currency-toggle">
            {currencies.map((c) => (
              <button
                key={c}
                onClick={() => setCur(c)}
                data-testid={`pricing-currency-${c}`}
                style={cur === c ? { backgroundColor: accent, color: "#fff" } : undefined}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${cur === c ? "" : "text-slate-500 hover:text-slate-800"}`}
              >
                {symbols[c] || ""} {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {packages.map((p, i) => {
            const price = p.price?.[cur];
            return (
              <Reveal key={p.id} delay={(i % 4) * 0.06}>
                <div
                  className={`h-full p-7 rounded-2xl border flex flex-col hover-lift ${p.highlight ? "border-brand bg-white soft-shadow-lg ring-1 ring-brand/20" : "border-slate-200 bg-white card-shadow"}`}
                  data-testid={`bundle-${p.id}`}
                >
                  {p.highlight && <span className="self-start text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-brand px-2.5 py-1 rounded-full mb-3">Most Popular</span>}
                  <h3 className="font-heading text-lg font-bold text-slate-900">{p.name}</h3>
                  <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{p.tagline}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    {p.custom || price == null ? (
                      <span className="font-heading text-3xl font-extrabold text-slate-900">Custom</span>
                    ) : (
                      <>
                        <span className="font-heading text-3xl font-extrabold text-slate-900">{symbols[cur]}{fmt(price, cur)}</span>
                        <span className="text-slate-400 text-sm font-semibold">/{p.period || "mo"}</span>
                      </>
                    )}
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex gap-2 text-sm text-slate-600">
                        <Check size={15} className="text-green-600 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => onSelect?.(p, cur)}
                    data-testid={`bundle-cta-${p.id}`}
                    className={`mt-6 w-full py-3 rounded-xl flex items-center justify-center gap-2 ${p.highlight ? "btn-primary" : "btn-outline"}`}
                  >
                    {p.custom ? "Talk to Us" : "Get Started"} <ArrowRight size={15} />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="text-center">
          <p className="text-slate-400 text-sm mt-8 inline-flex items-center gap-2">
            <Sparkles size={14} /> Not sure which fits? Let the AI Growth Advisor recommend one for your business.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
